import React, { forwardRef, PropsWithChildren, useEffect, useState } from "react";
import useStore, { ClasswizeState } from "@/stores/index";
import { User, userDisplayText } from "@/js/utils/userUtils";
import { getClassroomIdForChat, getChatDisabled, isChatFeatureEnabled } from "@/js/utils/chatUtils";
import SendMessageModal from "../SendMessageModal";
import { StudentTile } from "../StudentTile";
import { isPauseInternetEnabled } from "@/stores/classwizeConfigStore";
import { StudentTileHeader } from "../StudentTile/StudentTileHeader";
import { StudentTileContent } from "../StudentTile/StudentTileContent";
import { togglePauseForStudent } from "@/api/pause";
import { togglePauseForStudentInState } from "../RegularClassroom/utils";
import { StudentTileButton } from "./components/StudentTileButton";
import { useClassroomPolicyStore } from "@/stores/v2/classroomPolicyStore";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import { isPlatformChatSupported } from "@/js/utils/chatUtils";
import { WindowData } from "@/js/components/RegularClassroomUser/utils";
import { ClassroomDetails } from "@/stores/classroomStore_I";
import { IGetSignatureDbReturn } from "@/stores/signatureStore_I";

interface RegularClassroomUserProps {
	classroom: ClassroomDetails;
	user: User;
	windowData: WindowData;
	deviceIndex: number;
	view: "tabs" | "screens";
	classIsActive: boolean;
	canCloseTab: boolean;
	showOpenTabBtn: boolean;
	showStudentAnnouncementBtn: boolean;
	signatureDb: IGetSignatureDbReturn;
	tabItemLimit: number;
	studentsAreFocused: boolean;
	studentIsFocused: boolean;
	isUpdatingFocus: boolean;
	activeFocusIsLocked: boolean;
	studentIsBeingScreenshared: boolean;
	handleToggleFocusForStudent: (stableIdV1: string) => void;
	openNewTabModal: (user: User) => void;
	openP2pModal: (
		user: User,
		tabId: string | undefined,
		chromeId: string | undefined,
		windowId: string | undefined,
		isActiveTab: boolean
	) => void;
	classOpenTime?: number;
}

const RegularClassroomUser = forwardRef<HTMLDivElement, PropsWithChildren<RegularClassroomUserProps>>(
	(props: RegularClassroomUserProps, ref) => {
		const [singleMessageModalOpen, setSingleMessageModalOpen] = useState(false);
		const [disabledChat, setDisabledChat] = React.useState(true);

		const classroomSourceId = useUserSessionStore((state) => state.getActiveGroup());
		const threadKey = useStore((state: ClasswizeState) => state.chat.getThreadKey(props.classroom, props.user));

		const sendMessageModalTitle = React.useMemo(() => {
			return `Send message to ${userDisplayText(props.user)}`;
		}, [props.user]);

		const isChatSupported = React.useMemo(() => {
			return isPlatformChatSupported(props.windowData);
		}, [props.windowData]);

		const unsubClassroomStore = useStore.subscribe(
			(state: ClasswizeState) => [state.classroom.dataV2],
			(newState: ClasswizeState) => {
				handleClassroomUpdate();
				return newState;
			}
		);

		const refreshChatDisabled = () => {
			const chatIsDisabled = getChatDisabled(props.classroom.name);
			setDisabledChat(chatIsDisabled);
		};

		const handleClassroomUpdate = () => {
			refreshChatDisabled();
		};

		useEffect(() => {
			refreshChatDisabled();

			// Cleanup function to unsubscribe
			return () => {
				unsubClassroomStore();
			};
		});

		const handleTogglePause = () => {
			const teacherId = useStore.getState().classroom.getTeacherStableIdV1();
			const classroomId = props.classroom.stableIdV1;
			const studentId = props.user.username;

			togglePauseForStudent(teacherId, classroomId, studentId)
				.then(() => {
					togglePauseForStudentInState(props.user.username);
				})
				.catch(() => {
					console.error("Failed to toggle pause for student");
				});
		};

		const handleToggleFocus = () => {
			props.handleToggleFocusForStudent(props.user.stableIdV1);
		};

		const handleToggleSingleMessageClass = async () => {
			setSingleMessageModalOpen(!singleMessageModalOpen);
		};

		const handleSingleChat = (threadKey: string | undefined) => {
			// TODO refactor this logic block to immediately select classroom student and threadlist
			useStore.getState().chat.setSelectedClassId(getClassroomIdForChat(props.classroom) ?? "");
			useStore.getState().chat.setSelectedStudentName(props.user.username);

			// set selected thread
			useStore.getState().chat.setSelectedThreadKey(threadKey ?? "");

			if (!useStore.getState().chat.chatWindowOpen) {
				useStore.getState().chat.toggleChatWindow(false);
			}
		};

		const isStudentPaused = () => {
			return useClassroomPolicyStore.getState().getPausedStudents().indexOf(props.user.username) >= 0;
		};

		const studentDisplayName = userDisplayText(props.user);

		return (
			<StudentTile ref={ref} username={props.user.username}>
				<StudentTileHeader
					user={props.user}
					actionsJsx={
						<>
							{isChatFeatureEnabled() && !disabledChat && (
								<StudentTileButton
									tooltipText={
										!props.classIsActive
											? ""
											: !isChatSupported
												? "Chat not supported on this device"
												: `Start a Chat with ${userDisplayText(props.user)}`
									}
									icon="fa-message-lines"
									handleClick={() => handleSingleChat(threadKey)}
									disabled={!props.classIsActive || !isChatSupported}
									buttonTestId="student-chat-btn"
								/>
							)}
							{props.showStudentAnnouncementBtn && (
								<StudentTileButton
									tooltipText={!props.classIsActive ? "" : `Send a message to ${studentDisplayName}`}
									icon="fa-bullhorn"
									handleClick={handleToggleSingleMessageClass}
									disabled={!props.classIsActive}
									buttonTestId="student-announcement-btn"
								/>
							)}
							{props.showOpenTabBtn && (
								<StudentTileButton
									tooltipText={!props.classIsActive ? "" : `Open a tab for ${studentDisplayName}`}
									icon="fa-browser"
									handleClick={() => props.openNewTabModal(props.user)}
									disabled={!props.classIsActive || props.studentIsFocused}
									buttonTestId="student-opentab-btn"
								/>
							)}
							{isPauseInternetEnabled() && (
								<StudentTileButton
									tooltipText={!props.classIsActive ? "" : `${isStudentPaused() ? "Resume" : "Pause"} internet for ${studentDisplayName}`}
									icon="fa-pause"
									handleClick={handleTogglePause}
									disabled={!props.classIsActive}
									buttonTestId={"pause-student-btn"}
									isActive={isStudentPaused()}
									activeVariant="red-outline"
								/>
							)}
							{props.studentsAreFocused && (
								<StudentTileButton
									tooltipText={
										props.studentIsFocused
											? `End focus for ${studentDisplayName}`
											: !props.classIsActive
												? ""
												: props.activeFocusIsLocked && props.studentIsBeingScreenshared
													? `Cannot add ${studentDisplayName} to locked focus while screensharing`
													: `Add ${studentDisplayName} to the active focus`
									}
									icon="fa-arrows-to-eye"
									handleClick={handleToggleFocus}
									disabled={
										!props.classIsActive || (!props.studentIsFocused && props.activeFocusIsLocked && props.studentIsBeingScreenshared)
									}
									buttonTestId={"focus-student-btn"}
									isActive={props.studentIsFocused}
									activeVariant="blue-solid"
									actionIsLoading={props.isUpdatingFocus}
								/>
							)}
						</>
					}
				/>
				<StudentTileContent
					user={props.user}
					view={props.view}
					canCloseTab={props.canCloseTab}
					isClassActive={props.classIsActive}
					windowData={props.windowData}
					signatureDb={props.signatureDb}
					classroom={props.classroom}
					openP2pModal={props.openP2pModal}
					tabItemLimit={props.tabItemLimit}
					classOpenTime={props.classOpenTime}
				/>

				<SendMessageModal
					open={singleMessageModalOpen}
					title={sendMessageModalTitle}
					classroomSourceId={classroomSourceId}
					users={[props.user]}
					onClose={() => setSingleMessageModalOpen(false)}
					subtitle="Your message will appear on this student's screen. They will not be able to reply."
				/>
			</StudentTile>
		);
	}
);

export default RegularClassroomUser;
